import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import { useSettings } from '../context/SettingsContext';
import { formatCurrency } from '../utils/helpers';

const DEMO_SALES_EVENTS_KEY = 'saleswork_demo_sales_events';

const getDemoSalesEvents = () => {
  try {
    const raw = localStorage.getItem(DEMO_SALES_EVENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatEventDate = date => {
  if (!date) return 'Tidspunkt ikke registrert';

  try {
    return new Intl.DateTimeFormat('nb-NO', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(date));
  } catch {
    return 'Tidspunkt ikke registrert';
  }
};

const getEventOwnerLabel = (event, deals, users) => {
  const relatedDeal = deals.find(deal =>
    String(deal?.deal_id || deal?.id) === String(event?.dealId)
  );

  const relatedUser = users.find(user =>
    user?.user_id === relatedDeal?.owner_user_id ||
    user?.id === relatedDeal?.owner_user_id ||
    user?.user_id === relatedDeal?.owner_id ||
    user?.id === relatedDeal?.owner_id
  );

  return (
    event?.ownerName ||
    event?.owner ||
    event?.sellerName ||
    event?.userName ||
    relatedDeal?.ownerName ||
    relatedDeal?.owner ||
    relatedDeal?.owner_user_name ||
    relatedUser?.name ||
    'Ukjent selger'
  );
};

const Scoreboard = () => {
  const { users, deals, budgets } = useCRM();
  const { crmSettings } = useSettings();
  const [salesEvents, setSalesEvents] = useState(getDemoSalesEvents);

  useEffect(() => {
    const refreshSalesEvents = () => {
      setSalesEvents(getDemoSalesEvents());
    };

    window.addEventListener('storage', refreshSalesEvents);
    window.addEventListener('focus', refreshSalesEvents);

    return () => {
      window.removeEventListener('storage', refreshSalesEvents);
      window.removeEventListener('focus', refreshSalesEvents);
    };
  }, []);

  const closedWonEvents = useMemo(() => (
    salesEvents
      .filter(event => event?.type === 'closed_won')
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
  ), [salesEvents]);

  // 1. Kalkuler resultater for alle brukere (MÅ gjøres før noen 'return')
  const leaderboard = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];

    return users.map(user => {
      // Regn ut totalt vunnet beløp for denne selgeren
      const wonAmount = deals
        .filter(d => d.owner_user_id === user.user_id && d.status === 'Vunnet')
        .reduce((sum, d) => sum + (Number(d.value) || 0), 0);

      // Regn ut aktivt budsjett
      const activeBudgets = budgets.filter(b =>
        b.type === 'User' &&
        b.target_id === user.user_id &&
        b.start_date <= today &&
        b.end_date >= today
      );

      // Hent summen av alle "currency" mål, eller bruk fallback (gammelt format)
      const budgetAmount = activeBudgets.reduce((sum, b) => {
        if (b.goals && b.goals.length > 0) {
          const currencyGoals = b.goals.filter(g => g.metric === 'currency');
          return sum + currencyGoals.reduce((gSum, g) => gSum + Number(g.value), 0);
        }
        return sum + Number(b.amount || 0);
      }, 0);

      // Prosentvis oppnåelse
      const progress = budgetAmount > 0 ? (wonAmount / budgetAmount) * 100 : 0;

      return {
        ...user,
        wonAmount,
        budgetAmount,
        progress
      };
    }).sort((a, b) => b.wonAmount - a.wonAmount); // Sorter etter mest solgt
  }, [users, deals, budgets]);

  const sellerRows = useMemo(() => {
    const stats = closedWonEvents.reduce((acc, event) => {
      const seller = getEventOwnerLabel(event, deals, users);

      if (!acc[seller]) {
        acc[seller] = {
          seller,
          wonCount: 0,
          wonValue: 0,
          latestSaleAt: null
        };
      }

      acc[seller].wonCount += 1;
      acc[seller].wonValue += Number(event.value || 0);

      if (
        !acc[seller].latestSaleAt ||
        new Date(event.createdAt || 0) > new Date(acc[seller].latestSaleAt || 0)
      ) {
        acc[seller].latestSaleAt = event.createdAt;
      }

      return acc;
    }, {});

    return Object.values(stats)
      .map(row => ({
        ...row,
        user: leaderboard.find(user => user.name === row.seller)
      }))
      .sort((a, b) => b.wonValue - a.wonValue || b.wonCount - a.wonCount);
  }, [closedWonEvents, deals, users, leaderboard]);

  // 2. Returner feilmelding hvis scoreboard er deaktivert i innstillinger
  if (!crmSettings?.enableScoreboard) {
    return (
      <div className="p-8 text-center mt-20">
        <SafeIcon icon="🔒" className="text-6xl text-slate-300 mb-6 block mx-auto" />
        <h2 className="text-2xl font-black text-slate-900">Scoreboard er deaktivert</h2>
        <p className="text-slate-500 font-medium mt-2">Dette kan skrus på under Innstillinger -&gt; Generelt.</p>
      </div>
    );
  }

  // 3. Plukk ut topp 3 for pallen
  const top3 = leaderboard.slice(0, 3);

  // 4. Palle-konfigurasjon (2. plass venstre, 1. plass midten, 3. plass høyre)
  const podiumOrder = [
    { rank: 2, user: top3[1], height: 'h-32 md:h-40', color: 'bg-slate-300', icon: '🥈' },
    { rank: 1, user: top3[0], height: 'h-48 md:h-56', color: 'bg-yellow-400', icon: '👑' },
    { rank: 3, user: top3[2], height: 'h-24 md:h-32', color: 'bg-amber-600', icon: '🥉' }
  ];

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto min-h-screen">
      {/* Header */}
      <div className="text-center mb-6 mt-4 md:mt-6">
        <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight flex items-center justify-center gap-4">
          <SafeIcon icon="🏆" className="text-yellow-500" /> Wall of Fame
        </h1>
        <p className="text-slate-500 font-medium text-lg mt-3">Salgskonkurranse og resultater i sanntid.</p>
      </div>

      {/* Closed won sales */}
      <section className="mb-10 rounded-[2rem] border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 md:p-7 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Vunnet salg</p>
            <h2 className="mt-1 text-2xl font-black text-slate-900">Siste kunder på Wall of Fame</h2>
          </div>
          <span className="w-fit rounded-full bg-amber-100 px-3 py-1 text-xs font-black text-amber-700">
            {closedWonEvents.length} {closedWonEvents.length === 1 ? 'salg' : 'salg'}
          </span>
        </div>

        {closedWonEvents.length > 0 ? (
          <div className="mt-5 grid gap-3">
            {closedWonEvents.map(event => (
              <div
                key={event.id || `${event.dealId}-${event.createdAt}`}
                className="flex flex-col gap-4 rounded-2xl border border-white bg-white/90 p-4 shadow-sm md:flex-row md:items-center md:justify-between"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-xl">
                    <SafeIcon icon="🏆" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-black text-slate-900">
                        {event.companyName || 'Ukjent selskap'} ble kunde
                      </h3>
                      <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-emerald-700">
                        Vunnet salg / Kunde
                      </span>
                    </div>
                    {event.contactName && (
                      <p className="mt-1 text-sm font-medium text-slate-500">
                        Kontaktperson: {event.contactName}
                      </p>
                    )}
                    <p className="mt-1 text-xs font-medium text-slate-400">
                      {formatEventDate(event.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="text-left md:text-right">
                  <p className="font-black text-slate-900">
                    {formatCurrency(Number(event.value) || 0)}
                  </p>
                  <p className="mt-1 text-xs font-bold text-slate-500">
                    Eier: {getEventOwnerLabel(event, deals, users)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-5 rounded-2xl border border-dashed border-amber-200 bg-white/60 px-6 py-8 text-center">
            <SafeIcon icon="📈" className="mx-auto mb-3 block text-3xl" />
            <p className="text-sm font-medium leading-6 text-slate-500">
              Ingen vunne salg registrert ennå. Omgjør en lead til kunde for å vise den her.
            </p>
          </div>
        )}
      </section>

      {/* Podium */}
      <div className={`flex justify-center items-end gap-2 md:gap-6 mb-10 h-64 md:h-80 ${leaderboard.length === 0 ? 'hidden' : ''}`}>
        {podiumOrder.map((pos, i) => {
          if (!pos.user) return null;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.2, type: "spring", stiffness: 100 }}
              className="flex flex-col items-center w-24 md:w-32"
            >
              <div className="relative mb-4">
                <img
                  src={pos.user.avatar}
                  alt={pos.user.name}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full border-4 border-white shadow-xl object-cover z-10 relative bg-slate-100"
                />
                <div className="absolute -top-4 -right-4 text-2xl md:text-3xl z-20 drop-shadow-md">
                  <SafeIcon icon={pos.icon} />
                </div>
              </div>

              <div className="text-center mb-3">
                <p className="font-bold text-slate-900 text-xs md:text-sm truncate w-full px-1">
                  {pos.user.name.split(' ')[0]}
                </p>
                <p className="text-[10px] font-black text-slate-500 uppercase mt-0.5">
                  {formatCurrency(pos.user.wonAmount, true)}
                </p>
              </div>

              <div className={`w-full ${pos.height} ${pos.color} rounded-t-2xl shadow-inner flex items-start justify-center pt-4 relative overflow-hidden border border-black/5`}>
                <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent"></div>
                <span className="text-white font-black text-3xl md:text-4xl opacity-90 drop-shadow-md relative z-10">
                  {pos.rank}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Leaderboard Table */}
      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-16 text-center">#</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Selger</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden md:table-cell">Måloppnåelse (Omsetning)</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Vunnet (NOK)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {sellerRows.map((row, index) => {
                const user = row.user || {
                  name: row.seller,
                  role: 'Selger',
                  avatar: '',
                  budgetAmount: 0,
                  progress: 0
                };

                return (
                  <motion.tr
                    key={row.seller}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + (index * 0.05) }}
                    className="hover:bg-slate-50 transition-colors group"
                  >
                    <td className="px-6 py-5 text-center">
                      <span className={`text-sm font-black ${index < 3 ? 'text-slate-900' : 'text-slate-400'}`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full border border-slate-200 shadow-sm" />
                        <div>
                          <p className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors">{user.name}</p>
                          <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{user.role}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 w-1/3 hidden md:table-cell">
                      {user.budgetAmount > 0 ? (
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-1000 ${user.progress >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                              style={{ width: `${Math.min(user.progress, 100)}%` }}
                            />
                          </div>
                          <span className={`text-xs font-bold w-12 text-right ${user.progress >= 100 ? 'text-emerald-600' : 'text-slate-600'}`}>
                            {user.progress.toFixed(0)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 font-medium bg-slate-100 px-2 py-1 rounded-md">Ingen budsjett</span>
                      )}
                    </td>
                    <td className="px-6 py-5 text-right">
                      <span className="font-black text-slate-900 text-base">{formatCurrency(row.wonValue)}</span>
                    </td>
                  </motion.tr>
                );
              })}

              {sellerRows.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center">
                    <div className="mx-auto max-w-md rounded-2xl border border-dashed border-blue-200 bg-blue-50/60 px-6 py-8 shadow-sm">
                      <SafeIcon icon="📈" className="mb-4 block text-4xl mx-auto" />
                      <h2 className="text-base font-black text-slate-900">Ingen salgsdata ennå</h2>
                      <p className="mt-2 text-sm font-medium leading-6 text-slate-500">
                        Ingen vunne salg registrert ennå. Omgjør en lead til kunde for å vise den her.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Scoreboard;